<script lang="ts" module>
	export interface Tabs {
		id: string;
		title: string;
		disabled?: boolean;
	}
</script>

<script lang="ts">
	import { fade } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import type { TxtVariant } from '../Txt';
	import { TabsHeader } from './TabsHeader';

	let {
		showIndex = false,
		onClickTabDisabled = undefined,
		disabled = undefined,
		fullLine = false,
		headerVariant = 'label2',
		dataCy,
		activeTab = $bindable(),
		tabs = [],
		onClickTab = undefined,
		classes = {},
		tabsHeader,
		children,
		...rest
	}: Props = $props();

	interface Props {
		class?: string;
		showIndex?: boolean;
		onClickTabDisabled?: ((index: number) => void) | undefined;
		disabled?: boolean | undefined;
		fullLine?: boolean;
		headerVariant?: TxtVariant;
		dataCy: string;
		activeTab: number;
		tabs?: Tabs[];
		tabsHeader?: import('svelte').Snippet;
		children?: import('svelte').Snippet;
		onClickTab?: ((index: number, previousTab?: number) => void) | undefined;
		classes?: {
			index?: string;
			header?: string;
			container?: string;
			button?: string;
			currentTabBorder?: string;
			inactiveTabLabel?: string;
			disabledTabLabel?: string;
			tabLabel?: string;
		};
	}

	let dataCyPrefix = $derived(`${dataCy}-tabs`);

	const setActiveTab = (index: number) => {
		const activeTabCopy = activeTab;
		if (!disabled) {
			activeTab = index;
			if (onClickTab) {
				onClickTab(index, activeTabCopy);
			}
		} else {
			if (onClickTabDisabled) {
				onClickTabDisabled(index);
			}
		}
	};
</script>

<div data-cy={dataCyPrefix} {...rest}>
	<TabsHeader
		{tabs}
		{showIndex}
		variant={headerVariant}
		{setActiveTab}
		{activeTab}
		{dataCy}
		{classes}
		class={classes.header}
	/>

	{@render tabsHeader?.()}

	{#if fullLine}
		<div role="separator" class="mt-[-1px] h-[1px] w-full bg-gray-200"></div>
	{/if}

	{#each tabs as _tab, index}
		{#if index === activeTab}
			<div
				class={twMerge('mt-4', classes.container)}
				data-cy={`${dataCyPrefix}-content`}
				in:fade={{ duration: 500 }}
			>
				<!-- Tab content comes from `children`. Tabs used to render a per-tab
				`markdown` field itself, which put the Markdown renderer (~72 KiB) on every
				page with tabs, including those that never used it. -->
				{@render children?.()}
			</div>
		{/if}
	{/each}
</div>
